import type { DateTime } from "luxon";
import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FilterFieldList } from "../../../components/UIToolkit/Filters/FilterFieldList";
import { FilterSidebar } from "../../../components/UIToolkit/Filters/FilterSidebar";
import { buildFacetSections, type FacetSpec } from "../../../components/UIToolkit/Filters/facetSections";
import type { FilterSection } from "../../../components/UIToolkit/Filters/filterFields";
import type { FacetFiltersState } from "../../../hooks/useFacetFilters";
import { STATUS } from "../../../theme/tokens";
import { NOT_SET_KEY } from "../../../utils/facetFilters";
import { useFacetLabels } from "../pbjFacetLabels";
import { STAFFING_DIMENSIONS, StaffingFacet, type StaffingGridRow, StaffingReviewFlag } from "./staffingHoursFilters";

const REVIEW_LABEL_KEY: Record<StaffingReviewFlag, string> = {
	[StaffingReviewFlag.NoPbjJob]: "pbj_model.staffing.filter.review.no_pbj_job",
	[StaffingReviewFlag.NoPayType]: "pbj_model.staffing.filter.review.no_pay_type",
	[StaffingReviewFlag.Capped]: "pbj_model.staffing.filter.review.capped",
	[StaffingReviewFlag.Dst]: "pbj_model.staffing.filter.review.dst",
	[StaffingReviewFlag.PayTypeSetByHand]: "pbj_model.staffing.filter.review.pay_type_set_by_hand",
};

const isReviewFlag = (value: string): value is StaffingReviewFlag => (Object.values(StaffingReviewFlag) as string[]).includes(value);

type Props = {
	rows: StaffingGridRow[];
	filters: FacetFiltersState<StaffingGridRow>;
	/** The Building group, first in the rail; empty outside the network view. */
	buildingFacets: FacetSpec<StaffingGridRow>[];
	activeFilterCount: number;
	onClearAll: () => void;
	collapsed: boolean;
	onCollapsedChange: (collapsed: boolean) => void;
	hideSourceSpecificColumns: boolean;
	periodStart: DateTime;
	periodEnd: DateTime;
	/** Unset = the period's own edge. */
	windowStart: DateTime | undefined;
	windowEnd: DateTime | undefined;
	onWindowStartChange: (date: DateTime | undefined) => void;
	onWindowEndChange: (date: DateTime | undefined) => void;
};

/** Staffing Hours filters: "Needs review" checks, a date window, then one group per filterable column. */
export const StaffingHoursFilterRail: FC<Props> = ({
	rows,
	filters,
	buildingFacets,
	activeFilterCount,
	onClearAll,
	collapsed,
	onCollapsedChange,
	hideSourceSpecificColumns,
	periodStart,
	periodEnd,
	windowStart,
	windowEnd,
	onWindowStartChange,
	onWindowEndChange,
}) => {
	const { t } = useTranslation();
	const labels = useFacetLabels();

	const pbjJobLabels = useMemo(() => {
		const byId = new Map<string, string>();
		for (const r of rows) {
			if (r.pbjJobId != null && !byId.has(r.pbjJobId)) byId.set(r.pbjJobId, [r.payTitleCode, r.pbjJobDescription].filter(Boolean).join(" - "));
		}
		return byId;
	}, [rows]);

	const sections = useMemo<FilterSection[]>(() => {
		const d = STAFFING_DIMENSIONS;
		const facets: FacetSpec<StaffingGridRow>[] = [
			...buildingFacets,
			{
				dimension: d[StaffingFacet.Review],
				title: t("pbj_model.filter.needs_review"),
				getLabel: (key) => (isReviewFlag(key) ? t(REVIEW_LABEL_KEY[key]) : key),
				colorDot: STATUS.WARNING.main,
				order: Object.values(StaffingReviewFlag),
			},
			{
				dimension: d[StaffingFacet.DayType],
				title: t("pbj_model.filter.day_type"),
				getLabel: labels.dayType,
			},
			{
				dimension: d[StaffingFacet.PbjJob],
				title: t("pbj_model.staffing.col.pbj_job"),
				// DEFAULT: every id key comes from these rows, so the raw id only shows if that ever stops holding.
				getLabel: (key) => (key === NOT_SET_KEY ? t("pbj_model.staffing.filter.review.no_pbj_job") : (pbjJobLabels.get(key) ?? key)),
				searchable: true,
			},
			{ dimension: d[StaffingFacet.JobDescription], title: t("pbj_model.staffing.col.job_description"), getLabel: labels.raw, searchable: true },
			{ dimension: d[StaffingFacet.Department], title: t("pbj_model.staffing.col.department_name"), getLabel: labels.raw },
			{ dimension: d[StaffingFacet.PayType], title: t("pbj_model.staffing.col.pay_type_code"), getLabel: labels.payType },
			{ dimension: d[StaffingFacet.Agency], title: t("pbj_model.staffing.col.agency_name"), getLabel: labels.raw, searchable: true },
			{
				dimension: d[StaffingFacet.EmploymentType],
				title: t("pbj_model.staffing.col.employment_type"),
				getLabel: labels.employmentType,
				defaultExpanded: false,
			},
			{ dimension: d[StaffingFacet.WorkerType], title: t("pbj_model.staffing.col.w2_or_1099"), getLabel: labels.raw, defaultExpanded: false },
			{
				dimension: d[StaffingFacet.EarnCode],
				title: t("pbj_model.staffing.col.earn_code"),
				getLabel: labels.raw,
				defaultExpanded: false,
				hidden: hideSourceSpecificColumns,
			},
		];
		const builtSections = buildFacetSections({
			rows,
			facets,
			result: filters.result,
			selection: filters.selection,
			onToggle: filters.toggle,
			highlightedId: filters.highlightedId,
			dataTestId: "pbj-staffing-filter",
		});
		const buildingSections = builtSections.slice(0, buildingFacets.length);
		const [review, ...attributes] = builtSections.slice(buildingFacets.length);
		const dates: FilterSection = {
			id: "dates",
			title: t("pbj_model.staffing.filter.dates"),
			activeCount: (windowStart ? 1 : 0) + (windowEnd ? 1 : 0),
			fields: [
				{
					kind: "date",
					id: "dates-from",
					label: t("pbj_model.staffing.filter.from"),
					value: windowStart,
					onChange: onWindowStartChange,
					minDate: periodStart,
					// DEFAULT: with no end picked, the start can run to the period's last day.
					maxDate: windowEnd ?? periodEnd,
				},
				{
					kind: "date",
					id: "dates-to",
					label: t("pbj_model.staffing.filter.to"),
					value: windowEnd,
					onChange: onWindowEndChange,
					// DEFAULT: with no start picked, the end can run back to the period's first day.
					minDate: windowStart ?? periodStart,
					maxDate: periodEnd,
				},
			],
		};
		return [...buildingSections, review, dates, ...attributes];
	}, [
		t,
		labels,
		rows,
		filters,
		buildingFacets,
		pbjJobLabels,
		hideSourceSpecificColumns,
		periodStart,
		periodEnd,
		windowStart,
		windowEnd,
		onWindowStartChange,
		onWindowEndChange,
	]);

	return (
		<FilterSidebar
			collapsed={collapsed}
			onCollapsedChange={onCollapsedChange}
			collapsedVariant="rail"
			frame="flush"
			activeFilterCount={activeFilterCount}
			onClearAll={onClearAll}
			dataTestId="pbj-staffing-filter-sidebar"
		>
			<FilterFieldList sections={sections} dividers dataTestId="pbj-staffing-filter" />
		</FilterSidebar>
	);
};
